import {Outlet, useParams} from 'react-router-dom';
import {getAuth} from 'firebase/auth';
import {useAuthState} from 'react-firebase-hooks/auth';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {setDoc} from 'firebase/firestore';
import {CompletionContext, defaultCompletion} from 'contexts/CompletionContext';
import {getUserLessonsProgressMapRef} from 'course/lessons/userLessonsProgressMap';
import {getUserAssignmentProgressMapDocumentReference} from 'assignments/models/userAssignmentProgressMapReferences';
import {getParentCourseId} from '../general/utils/apputils';

/**
 * Mounted once above every child route of `/apply/:targetCourseId` (see
 * `Router.tsx`), so the splash, "About You", a lesson preview, and a math-IDE
 * assignment preview all share single `lessonsProgress`/`assnProgress`
 * subscriptions instead of each hop resubscribing.
 *
 * Provides the same {@link CompletionContext} the full course's
 * `CompletionProvider` does, but backed by only the two Firestore listeners a
 * preview actually reads (`lessonsProgress`, `assnProgress`) — the same
 * documents `ApplicationJoinLayout` already subscribes to for the checklist
 * badges, so marking a lesson/assignment complete here updates the checklist
 * immediately. Every other field falls back to {@link defaultCompletion}:
 * nothing in the apply flow or these previews reads section, self-check, or
 * teach-now-training progress.
 *
 * @returns The completion provider wrapping the child routes' `<Outlet/>`.
 */
export function ApplicationTaskProgressProvider() {
  const {targetCourseId} = useParams<{targetCourseId: string}>();
  const [user] = useAuthState(getAuth());
  const courseId = targetCourseId
    ? getParentCourseId(targetCourseId)
    : undefined;

  const lessonsProgressDocRef =
    user && courseId
      ? getUserLessonsProgressMapRef({userId: user.uid, courseId})
      : null;
  const [lessonsProgress, lessonsProgressLoading] = useDocumentData(
    lessonsProgressDocRef,
  );

  const assnProgressDocRef =
    user && courseId
      ? getUserAssignmentProgressMapDocumentReference({
          userId: user.uid,
          courseId,
        })
      : null;
  const [assnProgress, assnProgressLoading] = useDocumentData(
    assnProgressDocRef,
  );

  const isLessonCompleted = (lessonId: string) =>
    Boolean(lessonsProgress?.[lessonId]);
  const isLessonSlideCompleted = (lessonId: string, slideId: string) =>
    Boolean(lessonsProgress?.[`${lessonId}/${slideId}`]);
  const setLessonCompleted = async (lessonId: string) => {
    if (!lessonsProgressDocRef || !lessonId) {
      return;
    }
    await setDoc(lessonsProgressDocRef, {[lessonId]: true}, {merge: true});
  };
  const setLessonSlideCompleted = async (
    lessonId: string,
    slideId: string,
  ) => {
    if (!lessonsProgressDocRef) {
      return;
    }
    await setDoc(
      lessonsProgressDocRef,
      {[`${lessonId}/${slideId}`]: true},
      {merge: true},
    );
  };

  const isAssnCompleted = (assnId: string) => Boolean(assnProgress?.[assnId]);
  const setAssnCompleted = async (assnId: string) => {
    if (!assnProgressDocRef || !assnId) {
      return;
    }
    await setDoc(assnProgressDocRef, {[assnId]: true}, {merge: true});
  };

  return (
    <CompletionContext.Provider
      value={{
        ...defaultCompletion,
        isCompletionLoading: lessonsProgressLoading || assnProgressLoading,
        lessonsProgress: lessonsProgress ?? {},
        isLessonCompleted,
        isLessonSlideCompleted,
        setLessonCompleted,
        setLessonSlideCompleted,
        assnProgress: assnProgress ?? {},
        isAssnCompleted,
        setAssnCompleted,
      }}
    >
      <Outlet />
    </CompletionContext.Provider>
  );
}
